import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Badge } from '../../components/ui/badge';
import api from '../../services/api';
import { toast } from 'sonner';
import {
  Package, TrendingUp, Clock, Truck, CheckCircle, BarChart3, DollarSign
} from 'lucide-react';
import {
  BarChart, Bar, LineChart, Line, PieChart, Pie, Cell,
  XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid
} from 'recharts';

const COLORS = ['#4f46e5', '#0ea5e9', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6'];
const STATUS_MAP = {
  ISSUED: { label: 'Awaiting Receipt', color: 'bg-blue-100 text-blue-700' },
  PARTIALLY_RECEIVED: { label: 'Partially Received', color: 'bg-amber-100 text-amber-700' },
  FULLY_RECEIVED: { label: 'Fully Received', color: 'bg-green-100 text-green-700' },
};

export default function GRNDashboard({ onNavigate }) {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  useEffect(() => {
    api.get('/api/grn/dashboard/summary').then(r => setData(r.data)).catch(() => toast.error('Failed to load dashboard'));
  }, []);

  if (!data) return <div className="text-center py-12 text-muted-foreground">Loading dashboard...</div>;

  const fmt = (v) => `$${Number(v || 0).toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;
  const kpis = [
    { label: 'Total GRNs', value: data.total_grns, icon: Package, color: 'text-indigo-600' },
    { label: 'GRNs This Month', value: data.grns_this_month, icon: TrendingUp, color: 'text-emerald-600' },
    { label: 'Pending Orders', value: data.pending_orders, icon: Clock, color: 'text-amber-600', action: () => onNavigate?.('orders') },
    { label: 'Value Received (Month)', value: fmt(data.value_received_this_month), icon: DollarSign, color: 'text-blue-600' },
    { label: 'Lines Received (Month)', value: data.lines_received_this_month, icon: CheckCircle, color: 'text-teal-600' },
    { label: 'Value Received (YTD)', value: fmt(data.value_received_this_year), icon: BarChart3, color: 'text-violet-600' },
  ];

  const statusData = (data.by_status || []).map((s, i) => ({
    name: STATUS_MAP[s.status]?.label || s.status, value: s.count, fill: COLORS[i % COLORS.length], rawStatus: s.status,
  }));

  const handlePieClick = (entry) => {
    if (entry?.rawStatus) onNavigate?.('orders', { status: entry.rawStatus });
  };

  const handleSupplierClick = (entry) => {
    if (entry?.supplier) onNavigate?.('orders', { search: entry.supplier });
  };

  return (
    <div className="space-y-6" data-testid="grn-dashboard">
      {/* KPIs */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
        {kpis.map(k => (
          <div key={k.label} onClick={k.action} data-testid={`grn-kpi-${k.label.toLowerCase().replace(/\s+/g, '-')}`}
            className={`border rounded-xl p-4 space-y-1.5 hover:shadow-sm transition-shadow ${k.action ? 'cursor-pointer hover:border-primary/40' : ''}`}>
            <div className="flex items-center gap-2">
              <div className={`p-1.5 rounded-lg bg-muted ${k.color}`}><k.icon className="h-4 w-4" /></div>
              <span className="text-[11px] font-medium text-muted-foreground leading-tight">{k.label}</span>
            </div>
            <p className="text-xl font-bold tracking-tight">{k.value}</p>
          </div>
        ))}
      </div>

      {/* Charts */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Monthly Trend */}
        <div className="border rounded-xl p-4">
          <h3 className="text-sm font-semibold mb-3">Receiving Activity (6 months)</h3>
          {(data.monthly_trend || []).length === 0 ? <p className="text-xs text-muted-foreground text-center py-8">No data</p> : (
            <ResponsiveContainer width="100%" height={220}>
              <LineChart data={data.monthly_trend}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                <XAxis dataKey="month" tick={{ fontSize: 11 }} />
                <YAxis yAxisId="left" tick={{ fontSize: 11 }} />
                <YAxis yAxisId="right" orientation="right" tick={{ fontSize: 11 }} tickFormatter={v => `$${(v / 1000).toFixed(0)}k`} />
                <Tooltip formatter={(v, n) => n === 'value' ? `$${v.toLocaleString()}` : v} />
                <Line yAxisId="left" type="monotone" dataKey="count" stroke="#4f46e5" strokeWidth={2} name="GRN Count" dot={{ r: 3 }} />
                <Line yAxisId="right" type="monotone" dataKey="value" stroke="#10b981" strokeWidth={2} name="Value" dot={{ r: 3 }} />
              </LineChart>
            </ResponsiveContainer>
          )}
        </div>

        {/* Orders by Status — Pie chart */}
        <div className="border rounded-xl p-4">
          <h3 className="text-sm font-semibold mb-3">Orders by Receiving Status</h3>
          <p className="text-[10px] text-muted-foreground -mt-2 mb-2">Click a segment to view orders</p>
          {statusData.length === 0 ? <p className="text-xs text-muted-foreground text-center py-8">No data</p> : (
            <ResponsiveContainer width="100%" height={220}>
              <PieChart>
                <Pie data={statusData} dataKey="value" nameKey="name" cx="50%" cy="50%" outerRadius={80} innerRadius={40}
                  label={({ name, value }) => `${name}: ${value}`} className="cursor-pointer"
                  onClick={(_, idx) => handlePieClick(statusData[idx])}>
                  {statusData.map((e, i) => <Cell key={i} fill={e.fill} />)}
                </Pie>
                <Tooltip />
              </PieChart>
            </ResponsiveContainer>
          )}
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Top Suppliers */}
        <div className="border rounded-xl p-4">
          <h3 className="text-sm font-semibold mb-3">Top Suppliers by Receipts</h3>
          <p className="text-[10px] text-muted-foreground -mt-2 mb-2">Click a bar to search orders by supplier</p>
          {(data.top_suppliers || []).length === 0 ? <p className="text-xs text-muted-foreground text-center py-8">No data</p> : (
            <ResponsiveContainer width="100%" height={220}>
              <BarChart data={(data.top_suppliers || []).slice(0, 8)} layout="vertical" margin={{ left: 80, right: 20 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                <XAxis type="number" tick={{ fontSize: 11 }} />
                <YAxis type="category" dataKey="supplier" tick={{ fontSize: 10 }} width={75} />
                <Tooltip />
                <Bar dataKey="count" fill="#0ea5e9" radius={[0, 4, 4, 0]} name="Receipts" className="cursor-pointer"
                  onClick={(entry) => handleSupplierClick(entry)} />
              </BarChart>
            </ResponsiveContainer>
          )}
        </div>

        {/* Recent GRNs */}
        <div className="border rounded-xl p-4">
          <h3 className="text-sm font-semibold mb-3">Recent Goods Receipts</h3>
          <p className="text-[10px] text-muted-foreground -mt-2 mb-2">Click a receipt to view the order</p>
          <div className="space-y-2 max-h-[220px] overflow-y-auto">
            {(data.recent_grns || []).length === 0 ? <p className="text-xs text-muted-foreground text-center py-8">No receipts yet</p> : (
              data.recent_grns.map(g => (
                <div key={g.grn_number} className="flex items-center justify-between p-2 rounded-lg hover:bg-muted/50 text-sm cursor-pointer hover:border-primary/30 border border-transparent transition-colors"
                  onClick={() => navigate(`/orders/${g.order_number || g.grn_number}`)} data-testid={`grn-recent-${g.grn_number}`}>
                  <div>
                    <span className="font-mono font-medium text-primary">{g.grn_number}</span>
                    <span className="text-muted-foreground ml-2 text-xs">{g.supplier_name}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-xs text-muted-foreground">{g.line_count} line{g.line_count !== 1 ? 's' : ''}</span>
                    <span className="font-mono text-xs">${g.value?.toLocaleString()}</span>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
